// TON address parsing, validation and display — deliberately free of
// @ton/core, which does not tree-shake and would weigh on the Mini App's
// startup for the sake of these few functions. See ./cell.ts for the encoding
// and the measurement.

import { decodeTonAddress, encodeTonAddress } from './cell';

/**
 * Whether a string is a well-formed TON mainnet address.
 *
 * Accepts every form the chain uses — raw (`0:<64 hex>`), bounceable (EQ…) and
 * non-bounceable (UQ…), in either base64 alphabet — because all of them name
 * the same account and a user pasting one from an exchange will have any.
 *
 * Testnet addresses are rejected. They decode cleanly and differ from mainnet
 * only by a flag bit, so accepting one as a recipient would send real funds to
 * an address nobody controls here.
 */
export function isValidTonAddress(address: string): boolean {
  const parts = decodeTonAddress(address);
  if (!parts) return false;
  if (parts.testOnly) return false;
  // Workchain 0 is the basechain, where all wallets live. -1 is the
  // masterchain: validators and system contracts, not user funds.
  return parts.workchain === 0;
}

/**
 * Canonical raw form (`0:<hex>`), for comparing two addresses that may be
 * written differently. Every form of one account normalises to one string.
 */
export function tonRawAddress(address: string): string {
  const parts = decodeTonAddress(address);
  if (!parts) throw new Error('ton: not an address');
  return `${parts.workchain}:${Buffer.from(parts.hash).toString('hex')}`;
}

/** Whether two TON addresses name the same account, whatever form each is in.
 *  False rather than throwing: it guards deposit matching, where one bad value
 *  from an indexer must not take the whole sync down. */
export function isSameTonAddress(a: string, b: string): boolean {
  const left = decodeTonAddress(a);
  const right = decodeTonAddress(b);
  if (!left || !right) return false;
  return (
    left.workchain === right.workchain &&
    Buffer.from(left.hash).equals(Buffer.from(right.hash))
  );
}

/**
 * The form a wallet shows for receiving: user-friendly and NON-bounceable.
 *
 * Bounceable (EQ…) is the same account with one flag changed, and a payment to
 * a bounceable address whose contract is not deployed is RETURNED to the
 * sender — which is exactly what a fresh wallet of ours is. Publishing that
 * form would bounce the user's first deposit back.
 */
export function formatTonAddress(rawOrFriendly: string, bounceable = false): string {
  const parts = decodeTonAddress(rawOrFriendly);
  if (!parts) throw new Error('ton: not an address');
  return encodeTonAddress(parts.workchain, parts.hash, bounceable);
}
